/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    getProductDetails,
    reorderProductsData,
    getEnabledRollUps,
    formatXAxisDates,
    formatTableDate
} from '../../../../utils/charts';
// material
import { Page, Content } from '@insticator/insticator-ui';
import Header from '../../../../material/structure/header/header';
import RevenueFilter from '../../../../material/globalfilter/globalfilter';
import PageHeader from '../../../../material/structure/pageheader/pageheader';
import PageContent from '../../../../material/structure/pagecontent/pagecontent';
import CardToggles from '../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../material/detailstable/detailstable';
import ChartLegend from '../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../material/chartreportsection/chartreportsection';
import LineChart from '../../../../material/linechart/linechart';
import MonetizationChartCards from '../chartcards/chartcards';
// styles
import injectSheet from 'react-jss';
import styles from './unitloadrpm_styles';



/* ========== ~~~~~~~~~~ MONETIZATION - UNIT LOAD RPM PAGE ~~~~~~~~~~ ========== */
const UnitLoadRPMPage = injectSheet(styles)((props) => {
    const { overview, details, lastUpdated, status } = useSelector((state) => state.revenue);
    const { filters } = useSelector((state) => state.app);

    const monetizationType = 'rpm';
    const monetizationTypeChangeRate = `${monetizationType}ChangeRate`;

    const changeType = filters.compareTo;

    const [selectedPeriodX, setSelectedPeriodX] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [revenueToggle, changeRevenueToggle] = useState('TOTAL');
	const [selectedPeriod, setSelectedPeriod] = useState('daily');
    const dataType = revenueToggle.toUpperCase(); // TOTAL, VIDEO, DISPLAY
    const period = `${selectedPeriod[0].toUpperCase()}${selectedPeriod.slice(1)}`; // Daily, Monthly, Weekly, Quarterly

    const cardtogglesList = [
        {id: 'TOTAL', label: 'Total RPM'}
    ];

    const revenueCharts = [
        {id: 'revenue', yType: 'dollar', changeType, title: 'Revenue', route: '/dashboard/monetization/revenue'},
        {id: 'rpm', yType: 'dollar', changeType, title: 'Unit Load RPM', route:'/dashboard/monetization/unitloadrpm'},
        {id: 'unitLoad', yType: '', changeType, title: 'Unit Loads', route: '/dashboard/monetization/unitloads'}
    ];

    const products = reorderProductsData(details[dataType][period]);
    const currentTotalDetail =  Object.keys(details[dataType][period].TOTAL);

    const [chartLegends, setChartLegends] = useState([]);
    const [isSelectedProductsChanged, setIsSelectedProductsChanged] = useState(false);
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);

    useEffect(() => {
        if(status === 'fulfilled' && chartLegends.length === 0) updateChartLegends();
    }, [products]);

    useEffect(() => {
        if(showTotal) updateChartLegends();
    }, [showTotal]);

    useEffect(() => {
        if(status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details['TOTAL']);
            setEnabledRollups(rollups);
            if(rollups.length > 0) setSelectedPeriod(rollups[0]);
        } else {
            setEnabledRollups([]);
            setSelectedPeriod('daily');
            setChartLegends([]);
        }
    }, [status]);

    useEffect(() => {
        // update chart legends values after selected products were changed
        if(isSelectedProductsChanged) {
            updateChartTotal();
            setIsSelectedProductsChanged(false);
        }
    }, [isSelectedProductsChanged]);

    useEffect(() => {
        // update chart total when click outside the chart
        if(!selectedDate) updateChartTotal();
    }, [selectedDate]);

    const updateChartTotal = () => {
        const legends = [...chartLegends];
        const totalValueIndex = chartLegends.map(product => product.id).indexOf('TOTAL');
        legends[totalValueIndex] = {
            ...legends[totalValueIndex],
            value:  selectedDate ? computeDateTotal(selectedDate) : computeTableTotal()
        }
        setChartLegends(legends);
    }

    const updateChartLegends = () => {
        const productArr = Object.keys(products).map(id => ({
            ...getProductDetails(id),
            value: overview['TOTAL'][id][monetizationType],
            changeRate: overview['TOTAL'][id][`${monetizationType}ChangeRate`],
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === id)[0].checked : true
        }))

        const totalIndex = productArr.map(product => product.id).indexOf('TOTAL');
        const orderedProducts = [...productArr].filter(product => product.id !== 'TOTAL');
        orderedProducts.sort((a, b) => {
            if(a.value > b.value) return -1;
            if(a.value < b.value) return 1;
            return 0;
        });

        if(totalIndex >= 0 && productArr[totalIndex]) orderedProducts.push({...productArr[totalIndex], value:  selectedDate ? computeDateTotal(selectedDate) : computeTableTotal()})

        setChartLegends(orderedProducts);
    }

    const renderChartLegends = () => {
        const totalValueIndex = chartLegends.map(product => product.id).indexOf('TOTAL');
        const totalValue = totalValueIndex >= 0 && chartLegends[totalValueIndex].value ? chartLegends[totalValueIndex].value : 0;

        return chartLegends.map((product, index) => {
            const productPercentage = totalValue > 0 ? ((product.value ? product.value : 0) / totalValue) * 100 : 0;
            const productPercentageText = totalValue && (product.value != totalValue) ? '(' + formatNumber(productPercentage, 'percent') + ')' : '';
            if(product.id === 'TOTAL' && chartLegends.length === 2) return null;
            return (
                <ChartLegend
                    title={`${product.name}`}
                    checkboxColor={product.id !== 'TOTAL' ? product.color : '#fff'}
                    checked={product.checked}
                    onCheck={checked => {
                        const legends = [...chartLegends];
                        legends[index] = {
                            ...legends[index],
                            checked: checked
                        }
                        setChartLegends(legends);
                        setIsSelectedProductsChanged(true);
                    }}
                    value={formatNumber(product.value, 'dollar')}
                    changeDirection={product.changeRate < 0 ? 'negative' : 'positive'}
                    change={formatNumber(product.changeRate ? product.changeRate : 0, 'percent')}
                    changeType={changeType}
                    metricType={'monetization'}
                />
            );
        })
    }

    const handleHoverOnRPMChart = rpmChartData => {
        const date = rpmChartData.data ? rpmChartData.data.origDate : rpmChartData.origDate;
        const legends = [...chartLegends];

        Object.keys(products).forEach(id => {
            let value = 0;
            let changeRate = 0;
            const productDetails = products[id] && products[id][date] ? products[id][date] : null;
            if(productDetails) {
                value = productDetails[monetizationType] ? productDetails[monetizationType] : 0;
                changeRate = productDetails[`${monetizationType}ChangeRate`]  ? productDetails[`${monetizationType}ChangeRate`] : 0;
            }

            const legendIndex = chartLegends.map(product => product.id).indexOf(id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        // Update total depending on selected products
        const totalValueIndex = chartLegends.map(product => product.id).indexOf('TOTAL');
        legends[totalValueIndex] = {
            ...legends[totalValueIndex],
            value: computeDateTotal(date)
        }

        setChartLegends(legends);
        setSelectedPeriodX(`${rpmChartData.data ? rpmChartData.data.formattedDate : rpmChartData.formattedDate}`)
        setSelectedDate(date);
        setShowTotal(false);
    }

    const computeDateTotal = date => {
        const isAllProductsSelected = chartLegends.filter(legend => legend.checked).length === chartLegends.length;
        if(isAllProductsSelected) return details['TOTAL'][period]['TOTAL'][date].rpm;

        const products = chartLegends.filter(legend => legend.checked && legend.id !== 'TOTAL').map(product => product.id);
        const isOneProductSelected = chartLegends.filter(legend => legend.checked && legend.id !== 'TOTAL').length === 1;
        if(isOneProductSelected) return details['TOTAL'][period][products[0]][date].rpm;

        let metrics = {
            revenue: 0,
            unitLoad: 0,
            rpm: 0
        }
        products.forEach(productId => {
            const dateDetails = details['TOTAL'][period][productId][date];
            metrics.revenue += dateDetails.revenue;
            metrics.unitLoad += dateDetails.unitLoad;
        })
        metrics.rpm = (metrics.revenue / metrics.unitLoad) * 1000;
        return metrics.rpm;
    }

    const computeTableTotal = () => {
        const products = chartLegends.filter(legend => legend.checked && legend.id !== 'TOTAL').map(product => product.id);
        if(products.length === 0) return  overview && overview['TOTAL']['TOTAL'] ? overview['TOTAL']['TOTAL'].rpm : 0;

        let metrics = {
            revenue: 0,
            unitLoad: 0,
            rpm: 0
        }
        products.forEach(productId => {
            const overviewDetails = overview['TOTAL'][productId];
            metrics.revenue += overviewDetails.revenue;
            metrics.unitLoad += overviewDetails.unitLoad;
        })
        metrics.rpm = (metrics.revenue / metrics.unitLoad) * 1000;
        return metrics.rpm;
    }

    const handleMouseLeave = () => {
        setShowTotal(true);
        setSelectedPeriodX('');
        setSelectedDate(null);
    }

    const cardtoggles = (
		<CardToggles>
            { cardtogglesList.map(card =>
                <CardToggle
                    label={card.label}
                    compareType={changeType}
                    metricType={'monetization'}
                    handleClick={() => {
                        changeRevenueToggle(card.id);
                        setChartLegends([]);
                    }}
                    percentChange={{
                        value: formatNumber(overview[card.id][monetizationTypeChangeRate], 'percent'),
                        direction: getChangeRateDirection(overview[card.id][monetizationTypeChangeRate])
                    }}
                    selected={revenueToggle === card.id}
                    value={formatNumber(overview[card.id][monetizationType], 'dollar')}
                />
            )}
		</CardToggles>
	);

    const formatDataForRPMReport = () => {
        let rpmChartData = [];
        const dateLength = products && products.TOTAL ? Object.keys(products.TOTAL).length : 10;
        Object.keys(products).forEach(id => {
            const checkedProducts = chartLegends.filter(product => product.checked === true);
            if(id === 'TOTAL') return;
            let items = [];
            if(checkedProducts.map(product => product.id).indexOf(id) >= 0) {
                Object.keys(products[id]).forEach((date, index) => {
                    // To allow duplicate x-axis labels // TODO: Refactor this
                    let spaces = '';
                    for(var i = 0; i < index; i++) spaces += ' ';
                    items.push({
                        x: formatXAxisDates(date, period, dateLength, 10).concat(spaces),
                        y: products[id][date][monetizationType],
                        origDate: date,
                        formattedDate: formatTableDate(date, period, products[id][date].startDate, products[id][date].endDate),
                        productName: getProductDetails(id).name
                    })
                })
            }
            rpmChartData.push({
                id,
                color: getProductDetails(id).color,
                data: items
            })
        });
        return rpmChartData;
    }

    const formattedData = formatDataForRPMReport();

    const hasSingleData = () => {
        let singleData = false;
        formattedData.forEach(item => {
            if(item.data.length === 1) singleData = true;
        })
        return singleData;
    }

    const chart = (
        <LineChart
            enablePoints={hasSingleData()}
            data={formattedData}
            onHover={handleHoverOnRPMChart}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            yType={'dollar'}
        />
    );

    const detailtable = (
        <DetailsTable
            tableType={'monetization'}
            dataType={monetizationType} // revenue, unitLoad, rpm
            adsType={dataType} // TOTAL, DISPLAY, VIDEO
            period={period} // Daily, Weekly, Monthly, Quarterly
            data={details}
            csvFileName={'Unit Load RPM'}
        />
    );

    const isNoData = () => {
        return  (status === 'rejected') ||
                (status === 'fulfilled' && currentTotalDetail.length === 0) ||
                (status === 'fulfilled' && overview[dataType].revenue === 0);
    }

    return (
        <Page name={'unitloadrpm'} klass={`${props.classes.unitloadrpm}`} >
            <Header>
                <RevenueFilter onFilterApply={props.dataRequest} />
            </Header>
            <Content>
                <PageHeader lastUpdatedTimestamp={lastUpdated} title={'Monetization'} subtitle={'Unit Load RPM'} dataTip={true} />
                <PageContent>
                    <MonetizationChartCards charts={revenueCharts} />
                    <ChartReportSection
                        legendTitle={selectedPeriodX}
                        title={'Total RPM'}
                        tooltipContent={
                            <div className={'tip-content'}>
                                <div className={'tip-title'}>
                                    <h4>Unit Load RPM</h4>
                                </div>
                                <div className={'tip-desc'}>
                                    <p>Unit Load RPM is the total revenue per thousand unit loads and is calculated by taking the total revenue, for the selected time period, and dividing it by the total number of unit loads, then multiplying by 1,000.</p>
                                    <p>Unit loads are the ad containers Insticator loads across each of your pages for all products including Full Ad Management, Commenting, and Trivia. For example, if we load one ad on the right rail, 2 ads on top of commenting, and another 2 ads at the bottom of commenting it would count as 2 unit loads.</p>
                                    <p>When looking at RPM in a rolled-up view of Daily, Weekly, Monthly, or Quarterly the calculation is the same but the time frame of the data will be driven by each period within the roll-up.</p>
                                </div>
                            </div>
                        }
                        cardtoggles={cardtoggles}
                        chart={chart}
                        loading={status === 'pending' ? true : false}
                        noData={isNoData()}
                        enabledPeriods={enabledRollups}
                        chartLegend={renderChartLegends()}
                        handleSelectPeriod={period => {
                            setSelectedPeriod(period);
                            handleMouseLeave();
                        }}
                        selectedPeriod={selectedPeriod}
                        detailTable={detailtable}
                    />
                </PageContent>
            </Content>
        </Page>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default UnitLoadRPMPage;
